// API клиент для управления группами доступа и RLS-привязками
import { apiClient } from './client'

// ---------------------------------------------------------------------------
// Response types (соответствуют backend schemas/access.py)
// ---------------------------------------------------------------------------

export interface AccessGroupResponse {
  id: string
  name: string
  zitadel_role_key: string
  description: string | null
  is_active: boolean
  org_id: string | null
  member_count: number
  created_at: string
  updated_at: string
}

export interface AccessGroupMember {
  user_id: string
  user_email: string | null
  user_full_name: string | null
  granted_by: string | null
  granted_at: string
}

export interface AccessGroupWithMembers extends AccessGroupResponse {
  members: AccessGroupMember[]
}

export interface UserRlsBinding {
  id: string
  user_id: string
  binding_type: 'fil' | 'podr' | 'srv' | 'otd'
  object_code: string
  granted_by: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export interface CreateGroupRequest {
  name: string
  zitadel_role_key: string
  description?: string
  org_id?: string | null
  is_active?: boolean
}

export interface UpdateGroupRequest {
  name?: string
  description?: string
  is_active?: boolean
}

export interface RlsBindingItem {
  binding_type: 'fil' | 'podr' | 'srv' | 'otd'
  object_code: string
}

export type RlsBindingType = 'fil' | 'podr' | 'srv' | 'otd'
export type RlsLookupType = RlsBindingType | 'usl_ok' | 'profil_mp' | 'profil_k'

export interface RlsOptionItem {
  code: string
  name: string
}

export interface RlsOptionsParams {
  type: RlsLookupType
  lpu_code: string
  fil?: string
  podr?: string
  srv?: string
  usl_ok?: string
  profil_mp?: string
  profil_k?: string
}

export interface BulkRlsRequest {
  bindings: RlsBindingItem[]
}

export interface BulkRlsResponse {
  bindings: UserRlsBinding[]
  deleted_count: number
  created_count: number
}

export interface EffectiveRlsRow {
  login: string
  userid: string
  zitadel_email: string
  access_level: 'fil' | 'podr' | 'srv' | 'otd'
  fil_name: string
  fil_code: number | null
  srv_name: string
  srv_code: number | null
  podr: string
  podr_name_short: string
  name_otd: string
  id_otd: string
  updated_at: string
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

export const fetchGroups = async (orgId?: string): Promise<AccessGroupResponse[]> => {
  const params = orgId ? { org_id: orgId } : {}
  const response = await apiClient.get<AccessGroupResponse[]>('/access-groups/', { params })
  return response.data
}

export const fetchGroup = async (groupId: string): Promise<AccessGroupWithMembers> => {
  const response = await apiClient.get<AccessGroupWithMembers>(`/access-groups/${groupId}`)
  return response.data
}

export const createGroup = async (req: CreateGroupRequest): Promise<AccessGroupResponse> => {
  const response = await apiClient.post<AccessGroupResponse>('/access-groups/', req)
  return response.data
}

export const updateGroup = async (
  groupId: string,
  req: UpdateGroupRequest
): Promise<AccessGroupResponse> => {
  const response = await apiClient.patch<AccessGroupResponse>(`/access-groups/${groupId}`, req)
  return response.data
}

export const deactivateGroup = async (groupId: string): Promise<AccessGroupResponse> => {
  const response = await apiClient.delete<AccessGroupResponse>(`/access-groups/${groupId}`)
  return response.data
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export const addMember = async (groupId: string, userId: string): Promise<AccessGroupMember> => {
  const response = await apiClient.post<AccessGroupMember>(
    `/access-groups/${groupId}/members`,
    { user_id: userId }
  )
  return response.data
}

export const removeMember = async (groupId: string, userId: string): Promise<void> => {
  await apiClient.delete(`/access-groups/${groupId}/members/${userId}`)
}

// ---------------------------------------------------------------------------
// RLS Bindings
// ---------------------------------------------------------------------------

export const fetchUserRlsBindings = async (userId: string): Promise<UserRlsBinding[]> => {
  const response = await apiClient.get<UserRlsBinding[]>('/access-groups/rls-bindings', {
    params: { user_id: userId },
  })
  return response.data
}

export const bulkReplaceRls = async (
  userId: string,
  req: BulkRlsRequest
): Promise<BulkRlsResponse> => {
  const response = await apiClient.put<BulkRlsResponse>(
    `/access-groups/rls-bindings/user/${userId}`,
    req
  )
  return response.data
}

export const deleteRlsBinding = async (bindingId: string): Promise<void> => {
  await apiClient.delete(`/access-groups/rls-bindings/${bindingId}`)
}

export const fetchEffectiveRlsRows = async (userId: string): Promise<EffectiveRlsRow[]> => {
  const response = await apiClient.get<EffectiveRlsRow[]>('/access-groups/rls-bindings/effective', {
    params: { user_id: userId },
  })
  return response.data
}

export const previewEffectiveRlsRows = async (
  userId: string,
  req: BulkRlsRequest
): Promise<EffectiveRlsRow[]> => {
  const response = await apiClient.post<EffectiveRlsRow[]>(
    `/access-groups/rls-bindings/effective/user/${userId}`,
    req
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Entries (DataLens entries bound to access groups)
// ---------------------------------------------------------------------------

export interface AccessGroupEntry {
  id: string
  access_group_id: string
  datalens_entry_id: string
  entry_type: string
  entry_title: string | null
  workbook_title: string | null
  collection_title: string | null
  created_at: string
}

export interface AddEntryRequest {
  datalens_entry_id: string
  entry_type?: string
  entry_title?: string | null
}

export interface DashboardAclTemplateEntry {
  datalens_entry_id: string
  entry_type: string
  entry_title: string | null
}

export interface DashboardAclTemplateSummary {
  id: string
  org_id: string | null
  source_group_id: string | null
  name: string
  description: string | null
  entry_count: number
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface DashboardAclTemplateDetail extends DashboardAclTemplateSummary {
  entries: DashboardAclTemplateEntry[]
}

export interface CreateDashboardAclTemplateRequest {
  name: string
  description?: string | null
  org_id?: string | null
  source_group_id?: string | null
  entries: DashboardAclTemplateEntry[]
}

export interface CreateDashboardAclTemplateFromGroupRequest {
  name: string
  description?: string | null
}

export interface UpdateDashboardAclTemplateRequest {
  name?: string
  description?: string | null
  entries?: DashboardAclTemplateEntry[]
}

export const fetchEntries = async (groupId: string): Promise<AccessGroupEntry[]> => {
  const response = await apiClient.get<AccessGroupEntry[]>(
    `/access-groups/${groupId}/entries`
  )
  return response.data
}

export const addEntry = async (
  groupId: string,
  req: AddEntryRequest
): Promise<AccessGroupEntry> => {
  const response = await apiClient.post<AccessGroupEntry>(
    `/access-groups/${groupId}/entries`,
    req
  )
  return response.data
}

export const deleteEntry = async (groupId: string, entryId: string): Promise<void> => {
  await apiClient.delete(`/access-groups/${groupId}/entries/${entryId}`)
}

// ---------------------------------------------------------------------------
// Dashboard ACL Templates
// ---------------------------------------------------------------------------

export const fetchDashboardAclTemplates = async (
  orgId?: string
): Promise<DashboardAclTemplateSummary[]> => {
  const response = await apiClient.get<DashboardAclTemplateSummary[]>('/access-groups/templates', {
    params: orgId ? { org_id: orgId } : {},
  })
  return response.data
}

export const fetchDashboardAclTemplate = async (
  templateId: string
): Promise<DashboardAclTemplateDetail> => {
  const response = await apiClient.get<DashboardAclTemplateDetail>(
    `/access-groups/templates/${templateId}`
  )
  return response.data
}

export const createDashboardAclTemplate = async (
  req: CreateDashboardAclTemplateRequest
): Promise<DashboardAclTemplateDetail> => {
  const response = await apiClient.post<DashboardAclTemplateDetail>('/access-groups/templates', req)
  return response.data
}

export const createDashboardAclTemplateFromGroup = async (
  groupId: string,
  req: CreateDashboardAclTemplateFromGroupRequest
): Promise<DashboardAclTemplateDetail> => {
  const response = await apiClient.post<DashboardAclTemplateDetail>(
    `/access-groups/templates/from-group/${groupId}`,
    req
  )
  return response.data
}

export const updateDashboardAclTemplate = async (
  templateId: string,
  req: UpdateDashboardAclTemplateRequest
): Promise<DashboardAclTemplateDetail> => {
  const response = await apiClient.patch<DashboardAclTemplateDetail>(
    `/access-groups/templates/${templateId}`,
    req
  )
  return response.data
}

export const deleteDashboardAclTemplate = async (templateId: string): Promise<void> => {
  await apiClient.delete(`/access-groups/templates/${templateId}`)
}

// ---------------------------------------------------------------------------
// Per-group sync status
// ---------------------------------------------------------------------------

export interface GroupSyncStatus {
  total_entries: number
  last_synced_at: string | null
  last_synced_by: string | null
}

export interface SyncEntriesResult {
  resolved_count: number
  skipped: number
}

export const fetchGroupSyncStatus = async (groupId: string): Promise<GroupSyncStatus> => {
  const response = await apiClient.get<GroupSyncStatus>(
    `/access-groups/${groupId}/sync-status`
  )
  return response.data
}

export const syncGroupEntries = async (groupId: string): Promise<SyncEntriesResult> => {
  const response = await apiClient.post<SyncEntriesResult>(
    `/access-groups/${groupId}/sync-entries`
  )
  return response.data
}

// ---------------------------------------------------------------------------
// Access Config (deploy, diff, status)
// ---------------------------------------------------------------------------

export interface AccessConfigDiffEntry {
  role_key: string
  status: string
  added_entries: string[]
  removed_entries: string[]
}

export interface AccessConfigDiff {
  has_changes: boolean
  has_user_changes?: boolean
  changes: AccessConfigDiffEntry[]
  user_changes?: Array<{
    user_key: string
    status: string
    added_entries: string[]
    removed_entries: string[]
  }>
  current_groups: number
  new_groups: number
  current_total_entries: number
  new_total_entries: number
  current_users?: number
  new_users?: number
  current_total_user_entries?: number
  new_total_user_entries?: number
}

export interface AccessConfigStatus {
  last_deploy_at: string | null
  last_deploy_by: string | null
}

export interface DeployResult {
  success: boolean
  message: string
  deployed_at: string
  groups_count: number
  entries_count: number
}

export const fetchAccessConfigDiff = async (): Promise<AccessConfigDiff> => {
  const response = await apiClient.get<AccessConfigDiff>('/access-config/diff')
  return response.data
}

export const fetchAccessConfigStatus = async (): Promise<AccessConfigStatus> => {
  const response = await apiClient.get<AccessConfigStatus>('/access-config/status')
  return response.data
}

export const deployAccessConfig = async (): Promise<DeployResult> => {
  const response = await apiClient.post<DeployResult>('/access-config/deploy')
  return response.data
}

// ---------------------------------------------------------------------------
// RLS Options (autocomplete from ClickHouse)
// ---------------------------------------------------------------------------

export const fetchRlsOptions = async (params: RlsOptionsParams): Promise<RlsOptionItem[]> => {
  const response = await apiClient.get<RlsOptionItem[]>('/rls-options', { params })
  return response.data
}
